import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Activity, Heart, Droplets, Wind, Scale, Thermometer } from "lucide-react";
import { api } from "../../api/client";
import { useFamily } from "../../contexts/FamilyContext";

const TYPES = [
    { value: "blood_pressure", label: "Blood Pressure", unit: "mmHg", Icon: Heart },
    { value: "blood_sugar", label: "Blood Sugar", unit: "mg/dL", Icon: Droplets },
    { value: "pulse", label: "Pulse", unit: "bpm", Icon: Activity },
    { value: "spo2", label: "SpO₂", unit: "%", Icon: Wind },
    { value: "weight", label: "Weight", unit: "kg", Icon: Scale },
    { value: "temperature", label: "Temperature", unit: "°C", Icon: Thermometer },
    { value: "hba1c", label: "HbA1c", unit: "%", Icon: Droplets },
    { value: "tsh", label: "TSH", unit: "mIU/L", Icon: Activity },
    { value: "ldl_cholesterol", label: "LDL Cholesterol", unit: "mg/dL", Icon: Droplets },
    { value: "hdl_cholesterol", label: "HDL Cholesterol", unit: "mg/dL", Icon: Droplets },
    { value: "cholesterol", label: "Total Cholesterol", unit: "mg/dL", Icon: Droplets },
    { value: "peak_flow", label: "Peak Flow", unit: "L/min", Icon: Wind },
    { value: "bmi", label: "BMI", unit: "kg/m²", Icon: Scale },
];

const MEAL_CTX = [
    { value: "", label: "—" },
    { value: "fasting", label: "Fasting" },
    { value: "post_meal", label: "Post-meal" },
    { value: "random", label: "Random" },
    { value: "bedtime", label: "Bedtime" },
];

const AddMeasurementModal = ({ open, onClose, defaultType, onSaved }) => {
    const { activeMember } = useFamily();
    const [type, setType] = useState(defaultType || "blood_pressure");
    const [systolic, setSystolic] = useState("");
    const [diastolic, setDiastolic] = useState("");
    const [value, setValue] = useState("");
    const [mealContext, setMealContext] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setType(defaultType || "blood_pressure");
            setSystolic("");
            setDiastolic("");
            setValue("");
            setMealContext("");
            setNotes("");
        }
    }, [open, defaultType]);

    if (!open) return null;

    const t = TYPES.find((x) => x.value === type) || TYPES[0];
    const isBP = type === "blood_pressure";

    const submit = async (e) => {
        e.preventDefault();
        const payload = {
            type,
            unit: t.unit,
            meal_context: mealContext || null,
            notes: notes || null,
            family_member_id: activeMember?.is_self ? null : (activeMember?.id || null),
        };
        if (isBP) {
            const s = parseFloat(systolic);
            const d = parseFloat(diastolic);
            if (!s || !d) return toast.error("Enter both systolic and diastolic");
            payload.value = { systolic: s, diastolic: d };
        } else {
            const v = parseFloat(value);
            if (!v && v !== 0) return toast.error("Enter a numeric value");
            payload.value = v;
        }
        try {
            setSubmitting(true);
            const { data } = await api.post("/health/measurements", payload);
            toast.success("Measurement logged");
            onSaved?.(data);
            onClose();
        } catch (err) {
            const det = err?.response?.data?.detail;
            toast.error(typeof det === "string" ? det : "Could not save");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-zinc-900/60 p-3 backdrop-blur-sm sm:items-center sm:p-6" data-testid="add-measurement-modal" onClick={onClose}>
            <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-zinc-100 bg-white shadow-2xl dark:border-white/10 dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-4 dark:border-white/10">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">New measurement</p>
                        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">Log a reading</h2>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-50 dark:hover:bg-white/5" data-testid="add-meas-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <form onSubmit={submit} className="space-y-4 p-6">
                    <label className="block">
                        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Type</span>
                        <select value={type} onChange={(e) => setType(e.target.value)} className="input-clean mt-1" data-testid="add-meas-type">
                            {TYPES.map((tt) => <option key={tt.value} value={tt.value}>{tt.label}</option>)}
                        </select>
                    </label>
                    {isBP ? (
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Systolic (mmHg)</span>
                                <input type="number" inputMode="decimal" value={systolic} onChange={(e) => setSystolic(e.target.value)} required placeholder="120" className="input-clean mt-1" data-testid="add-meas-sys" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Diastolic (mmHg)</span>
                                <input type="number" inputMode="decimal" value={diastolic} onChange={(e) => setDiastolic(e.target.value)} required placeholder="80" className="input-clean mt-1" data-testid="add-meas-dia" />
                            </label>
                        </div>
                    ) : (
                        <label className="block">
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Value ({t.unit})</span>
                            <input type="number" inputMode="decimal" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} required placeholder="e.g. 110" className="input-clean mt-1" data-testid="add-meas-value" />
                        </label>
                    )}
                    <label className="block">
                        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Meal context</span>
                        <select value={mealContext} onChange={(e) => setMealContext(e.target.value)} className="input-clean mt-1" data-testid="add-meas-meal">
                            {MEAL_CTX.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Notes (optional)</span>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Anything noteworthy…" className="input-clean mt-1" data-testid="add-meas-notes" />
                    </label>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                        Logging for: <strong>{activeMember?.is_self ? "Me" : activeMember?.full_name || "—"}</strong>
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5" data-testid="add-meas-cancel">Cancel</button>
                        <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="add-meas-save">
                            {submitting ? "Saving..." : "Save reading"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export { TYPES };
export default AddMeasurementModal;
